import React, { useState } from 'react';
import { Button, Box, Typography, Paper, Grid } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { useAuthStore } from '../store/authStore';
import { useThemeContext } from '../components/contexts/ThemeContext';

const TimelineComponent = ({ setFormData, formData }) => {
    const [comments, setComments] = useState(Array.isArray(formData.notes) ? formData.notes : []);
    const [editorValue, setEditorValue] = useState('');
    const { contactId: contactId } = useAuthStore();

    const userName = useAuthStore().fullName;
    const roles = useAuthStore().roles;
    const MAX_CHAR_LIMIT = 3950;
    const isViewOnly = roles?.includes('ACCOUNTANT');
    const { mode, toggleTheme } = useThemeContext();
    const darkTheme = mode === 'dark';

    const handleAddComment = () => {
        if (editorValue.trim() !== '') {
            if (editorValue.length <= MAX_CHAR_LIMIT) {
                const newComment = {
                    id: null,
                    note: editorValue,
                    contactId: formData.id,
                    tnoteTypeId: 21,
                    addedByContactId: contactId,
                    addedBy: userName,
                    timestamp: dayjs().format('DD/MM/YYYY HH:mm:ss')
                };
                const updatedComments = [...comments, newComment];
                setComments(updatedComments);
                setFormData({ ...formData, notes: updatedComments });
                setEditorValue('');
            } else {
                alert(`Limit znaków został przekroczony`);
            }
        }
    };

    const handleDeleteComment = (index) => {
        const updatedComments = comments.filter((_, i) => i !== index);
        setComments(updatedComments);
        setFormData({ ...formData, notes: updatedComments });
    };

    const getDayOfWeek = (timestamp) => {
        return dayjs(timestamp, 'DD/MM/YYYY HH:mm:ss').day();
    };

    let lastDay = null;
    let lastPosition = 'left';

    return (
        <Grid container rowSpacing={1} columnSpacing={{ xs: 2, sm: 2, md: 4 }}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '60px' }}>
                    <ReactQuill
                        value={editorValue}
                        onChange={setEditorValue}
                        theme="snow"
                        readOnly={isViewOnly}
                        placeholder="Dodaj komentarz..."
                        style={{ height: '200px', width: '100%' }}
                        modules={{
                            toolbar: [
                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                [{ 'color': [] }, { 'background': [] }],
                                ['bold', 'italic'],
                                ['blockquote', 'code-block'],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                [{ 'indent': '-1' }, { 'indent': '+1' }],
                                ['clean']
                            ],
                            clipboard: {
                                matchVisual: false,
                            }
                        }}
                        formats={[
                            'font', 'size', 'header',
                            'color', 'background',
                            'align',
                            'bold', 'italic', 'underline', 'strike',
                            'blockquote', 'code-block',
                            'list', 'bullet', 'indent',
                            'link', 'image'
                        ]}
                        className="custom-quill"
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isViewOnly}
                        onClick={handleAddComment}
                        sx={{ alignSelf: 'flex-end' }}
                    >
                        Dodaj Komentarz
                    </Button>
                </Box>
                <Timeline sx={{ mt: 4 }}>
                    {comments?.map((comment, index) => {
                        let dayOfWeek;
                        if (comment.tnoteTypeId === 21) {
                            dayOfWeek = getDayOfWeek(comment.timestamp);
                        } else {
                            dayOfWeek = getDayOfWeek(comment.noteDate);
                        }

                        if (dayOfWeek !== lastDay) {
                            lastDay = dayOfWeek;
                            lastPosition = lastPosition === 'left' ? 'right' : 'left';
                        }

                        const position = lastPosition;

                        return (
                            <TimelineItem key={index} position={position}>
                                <TimelineOppositeContent>
                                    <Typography variant="body2" color="textSecondary">
                                        {comment.tnoteTypeId === 21 ? comment.timestamp : comment.noteDate}
                                    </Typography>
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    <TimelineDot color="grey" />
                                    {index < comments.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Paper elevation={3}
                                        sx={{
                                            pt: 1,
                                            px: 2,
                                            pb: 2,
                                            backgroundColor: '#f5f5f5',
                                            position: 'relative',

                                            borderRight: comment.tnoteTypeId === 1
                                                ? '6px solid #9c27b0'
                                                : (comment.tnoteTypeId === 11
                                                    ? '6px solid pink'
                                                    : (comment.tnoteTypeId === 21
                                                        ? '6px solid #4caf50' // zielony odcień
                                                        : (comment.tnoteTypeId === 31
                                                            ? '6px solid #ff9800' // pomarańczowy odcień
                                                            : (comment.tnoteTypeId === 41
                                                                ? '6px solid #f44336' // czerwony odcień
                                                                : '6px solid #5c9edf')))),

                                            borderLeft: comment.tnoteTypeId === 1
                                                ? '6px solid #9c27b0'
                                                : (comment.tnoteTypeId === 11
                                                    ? '6px solid pink'
                                                    : (comment.tnoteTypeId === 21
                                                        ? '6px solid #4caf50' // zielony odcień
                                                        : (comment.tnoteTypeId === 31
                                                            ? '6px solid #ff9800' // pomarańczowy odcień
                                                            : (comment.tnoteTypeId === 41
                                                                ? '6px solid #f44336' // czerwony odcień
                                                                : '6px solid #5c9edf')))),
                                        }}>
                                        <Typography variant="body2" fontWeight="bold" color={darkTheme ? "black" : "inherit"}
                                            sx={{
                                                px: 1,
                                                mb: 1,
                                                display: 'flex',
                                                justifyContent: position === 'left' ? 'space-between' : 'flex-start',
                                                alignItems: 'center',
                                                flexDirection: position === 'left' ? 'row-reverse' : 'row'
                                            }}>
                                            {comment.tnoteTypeId === 1 && `${comment.addedBy} (dodatkowe uwagi do start księgowości)`}
                                            {comment.tnoteTypeId === 11 && `${comment.addedBy} (dodatkowe uwagi do zmiany księgowej)`}
                                            {comment.tnoteTypeId === 21 && `${comment.addedBy}`}
                                            {comment.tnoteTypeId === 31 && `${comment.addedBy} (komentarz do zakończenia współpracy)`}
                                            {comment.tnoteTypeId === 41 && `${comment.addedBy} (komentarz do sprawozdania)`}
                                            {!isViewOnly && (
                                                <Button
                                                    variant="outlined"
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() => handleDeleteComment(index)}
                                                    sx={{ marginLeft: position === 'right' ? 'auto' : 'none', marginRight: position === 'left' ? 'auto' : 'none' }}
                                                >
                                                    Usuń
                                                </Button>
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={darkTheme ? "black" : "inherit"}
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment.note || '') }}
                                            sx={{ whiteSpace: 'pre-wrap', px: 1, textAlign: 'left', fontSize: 16 }}
                                        />
                                    </Paper>
                                </TimelineContent>
                            </TimelineItem>
                        );
                    }).reverse()}
                </Timeline>
            </Grid>
        </Grid>
    );
};

export default TimelineComponent;